// DemandChartSection.tsx
import { GenericChartCard } from '@components/ui/Chart';
import { DatePopover } from '@components/ui/DateRange';
import { DataSheet, dataSheetTokens, type DataSheetFilterConfig } from '@components/ui/DataSheet';
import type { MetricSeriesConfig } from '@hooks/graph/useMetricChart';
import React from 'react';

import { formatDisplayRange } from './dateUtils';
import { formatCompactNumber } from './numberUtils';
import type { ChartPoint, DateRange, Mode } from './types';

const tokens = dataSheetTokens;

type DemandChartSectionProps = {
    range: DateRange;
    mode: Mode;
    channelFilterConfig: DataSheetFilterConfig;
    productFilterConfig: DataSheetFilterConfig;

    showCalendar: boolean;
    onOpenCalendar: () => void;
    onCloseCalendar: () => void;
    onApplyRange: (r: DateRange) => void;

    onChangeMode: (m: Mode) => void;

    totalSales: number;
    growthLabel: string;
    data: ChartPoint[];
    loading: boolean;
    error: string | null;
    onShare?: () => void;
    onAddToChat?: () => void;
};

const DemandChartSection: React.FC<DemandChartSectionProps> = ({
    range,
    mode,
    channelFilterConfig,
    productFilterConfig,
    showCalendar,
    onOpenCalendar,
    onCloseCalendar,
    onApplyRange,
    onChangeMode,
    totalSales,
    growthLabel,
    data,
    loading,
    error,
    onShare,
    onAddToChat,
}) => {
    const series: MetricSeriesConfig<ChartPoint>[] = [
        {
            id: 'sales',
            key: 'sales',
            label: 'Sales',
            color: '#2563eb',
            renderAs: 'line',
            strokeWidth: 2.5,
        },
        {
            id: 'forecast',
            key: 'forecast',
            label: 'Forecast',
            color: '#16a34a',
            renderAs: 'line',
            strokeWidth: 2,
            variant: 'dashed',
        },
    ];

    const filteredSeries =
        mode === 'sales' ? series.filter((s) => s.id === 'sales') : series;

    // Build subtitle with KPIs
    const subtitle = (
        <span>
            Total sales:{' '}
            <span style={{ fontWeight: 600, color: tokens.colors.textPrimary }}>
                {formatCompactNumber(totalSales)} units
            </span>{' '}
            · Growth:{' '}
            <span
                style={{
                    fontWeight: 600,
                    color: growthLabel.startsWith('-') ? 'rgb(225, 29, 72)' : 'rgb(22, 163, 74)',
                }}
            >
                {growthLabel}
            </span>{' '}
            compared to previous period
        </span>
    );

    return (
        <section style={{ marginBottom: tokens.spacing.lg }}>
            {/* Section Header */}
            <div style={{ marginBottom: tokens.spacing.md, display: 'flex', alignItems: 'baseline', gap: tokens.spacing.sm, flexWrap: 'wrap' }}>
                <h2 style={{ fontSize: 18, fontWeight: 600, color: tokens.colors.textPrimary }}>
                    Sales Tracker
                </h2>
                <span style={{ fontSize: 12, color: tokens.colors.textMuted }}>
                    {subtitle}
                </span>
            </div>

            {error ? (
                <div 
                    style={{ 
                        padding: tokens.spacing.md, 
                        borderRadius: 8, 
                        backgroundColor: 'rgba(254, 226, 226, 0.5)',
                        border: '1px solid rgba(254, 202, 202, 1)',
                        color: 'rgb(185, 28, 28)',
                        fontSize: 13,
                    }}
                >
                    <span style={{ fontWeight: 600 }}>Error:</span> {error}
                </div>
            ) : (
                <DataSheet
                    data={[]}
                    columns={[]}
                    contentType="chart"
                    title="Daily Sales"
                    subtitle={`Showing ${formatDisplayRange(range)}`}
                    onShare={onShare}
                    filters={[channelFilterConfig, productFilterConfig]}
                    onAddToChat={onAddToChat}
                    toolbarControls={
                        <div style={{ position: 'relative' }}>
                            <button
                                type="button"
                                onClick={onOpenCalendar}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: tokens.spacing.xs,
                                    height: tokens.dimensions.controlHeight,
                                    padding: `0 ${tokens.spacing.sm}px`,
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: tokens.colors.textPrimary,
                                    backgroundColor: tokens.colors.surface,
                                    border: `1px solid ${tokens.colors.border}`,
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                }}
                            >
                                <span style={{ fontSize: 12 }}>📅</span>
                                <span>{formatDisplayRange(range)}</span>
                            </button>
                            {showCalendar && (
                                <div style={{ position: 'absolute', zIndex: 50, marginTop: 4 }}>
                                    <DatePopover
                                        range={range}
                                        onClose={onCloseCalendar}
                                        onApply={(r) => onApplyRange(r)}
                                    />
                                </div>
                            )}
                        </div>
                    }
                    isLoading={loading}
                    minHeight={280}
                >
                    {/* Chart content */}
                    <div style={{ padding: tokens.spacing.md }}>
                        <GenericChartCard<ChartPoint>
                            kind="line"
                            title=""
                            subtitle=""
                            data={data}
                            dimensionKey="date"
                            dimensionType="date"
                            series={filteredSeries}
                            xLabel="Date"
                            yLabel="Units"
                            controls={{
                                showToolbar: false,
                                showRange: false,
                                showSeriesToggles: false,
                            }}
                            initialRange="all"
                            height={260}
                            loading={loading}
                        />
                    </div>
                </DataSheet>
            )}
        </section>
    );
};

export default DemandChartSection;
